import { supabase } from '@/lib/supabase'
import type {
  SpaceConflictTableRow,
  SpaceConflictUpdate,
  SpaceConflictFilters,
  ConflictStatus,
  ConflictSeverity,
  ResolutionType,
} from '@/types/space-conflicts'

/**
 * Fetch all space conflicts with optional filtering
 * Includes joins to locations and units
 */
export async function fetchSpaceConflicts(
  organizationId: string,
  filters?: SpaceConflictFilters
): Promise<SpaceConflictTableRow[]> {
  let query = supabase
    .from('space_conflicts')
    .select(`
      id,
      organization_id,
      location_id,
      unit_id,
      flight_schedule_id,
      trigger_type,
      conflict_date,
      detected_at,
      severity,
      shortfall_sqft,
      affected_transient_ids,
      status,
      resolution_type,
      resolution_notes,
      resolved_at,
      resolved_by,
      created_at,
      location:location_id (
        name
      ),
      unit:unit_id (
        unit_code
      )
    `)
    .eq('organization_id', organizationId)
    .order('conflict_date', { ascending: false })

  // Apply filters
  if (filters?.status && filters.status.length > 0) {
    query = query.in('status', filters.status)
  }

  if (filters?.severity && filters.severity.length > 0) {
    query = query.in('severity', filters.severity)
  }

  if (filters?.locationId) {
    query = query.eq('location_id', filters.locationId)
  }

  if (filters?.dateFrom) {
    query = query.gte('conflict_date', filters.dateFrom)
  }

  if (filters?.dateTo) {
    query = query.lte('conflict_date', filters.dateTo)
  }

  if (filters?.search) {
    const search = filters.search.toLowerCase()
    query = query.or(`unit_id.ilike.%${search}%`)
  }

  const { data, error } = await query

  if (error) throw error

  // Transform joined data to flat structure
  return (data ?? []).map((row): SpaceConflictTableRow => {
    const location = (row.location as unknown as { name: string } | null)
    const unit = (row.unit as unknown as { unit_code: string } | null)
    const affectedIds = row.affected_transient_ids
      ? Array.isArray(row.affected_transient_ids)
        ? row.affected_transient_ids
        : typeof row.affected_transient_ids === 'string'
          ? [row.affected_transient_ids]
          : null
      : null

    return {
      id: row.id,
      organization_id: row.organization_id,
      location_id: row.location_id,
      unit_id: row.unit_id,
      flight_schedule_id: row.flight_schedule_id,
      location_name: location?.name ?? null,
      unit_code: unit?.unit_code ?? null,
      trigger_type: row.trigger_type,
      conflict_date: row.conflict_date,
      detected_at: row.detected_at,
      severity: row.severity as ConflictSeverity,
      shortfall_sqft: row.shortfall_sqft,
      affected_transient_ids: affectedIds,
      status: row.status as ConflictStatus,
      resolution_type: row.resolution_type as ResolutionType | null,
      resolution_notes: row.resolution_notes,
      resolved_at: row.resolved_at,
      resolved_by: row.resolved_by,
      created_at: row.created_at,
    }
  })
}

/**
 * Fetch a single space conflict by ID with full details
 */
export async function fetchSpaceConflictById(id: string): Promise<SpaceConflictTableRow> {
  const { data, error } = await supabase
    .from('space_conflicts')
    .select(`
      id,
      organization_id,
      location_id,
      unit_id,
      flight_schedule_id,
      trigger_type,
      conflict_date,
      detected_at,
      severity,
      shortfall_sqft,
      affected_transient_ids,
      status,
      resolution_type,
      resolution_notes,
      resolved_at,
      resolved_by,
      created_at,
      location:location_id (
        name
      ),
      unit:unit_id (
        unit_code
      )
    `)
    .eq('id', id)
    .single()

  if (error) throw error

  const location = (data.location as unknown as { name: string } | null)
  const unit = (data.unit as unknown as { unit_code: string } | null)
  const affectedIds = data.affected_transient_ids
    ? Array.isArray(data.affected_transient_ids)
      ? data.affected_transient_ids
      : typeof data.affected_transient_ids === 'string'
        ? [data.affected_transient_ids]
        : null
    : null

  return {
    id: data.id,
    organization_id: data.organization_id,
    location_id: data.location_id,
    unit_id: data.unit_id,
    flight_schedule_id: data.flight_schedule_id,
    location_name: location?.name ?? null,
    unit_code: unit?.unit_code ?? null,
    trigger_type: data.trigger_type,
    conflict_date: data.conflict_date,
    detected_at: data.detected_at,
    severity: data.severity as ConflictSeverity,
    shortfall_sqft: data.shortfall_sqft,
    affected_transient_ids: affectedIds,
    status: data.status as ConflictStatus,
    resolution_type: data.resolution_type as ResolutionType | null,
    resolution_notes: data.resolution_notes,
    resolved_at: data.resolved_at,
    resolved_by: data.resolved_by,
    created_at: data.created_at,
  }
}

/**
 * Resolve a space conflict with resolution details
 */
export async function resolveSpaceConflict(
  id: string,
  updates: SpaceConflictUpdate
): Promise<SpaceConflictTableRow> {
  const updateData: Record<string, unknown> = {}

  // Required fields
  updateData.status = updates.status

  // Optional fields
  if (updates.resolution_type !== undefined) {
    updateData.resolution_type = updates.resolution_type
  }
  if (updates.resolution_notes !== undefined) {
    updateData.resolution_notes = updates.resolution_notes
  }
  if (updates.resolved_at !== undefined) {
    updateData.resolved_at = updates.resolved_at
  }
  if (updates.resolved_by !== undefined) {
    updateData.resolved_by = updates.resolved_by
  }

  const { error } = await supabase
    .from('space_conflicts')
    .update(updateData)
    .eq('id', id)

  if (error) throw error

  // Fetch with full joins for return
  return fetchSpaceConflictById(id)
}
